// Traffic guide for "pet memorial ideas" (1,900/mo, KD 17) and "dog memorial
// ideas" (1,300/mo, KD 1), OpenSEO US 2026-11-02. Secondary: cat memorial
// ideas 320, sympathy gift for loss of dog 880, what to do with pet ashes 390,
// pet memorial garden ideas 260. The SERP is listicles, Reddit and shop pages,
// so one page covers dogs and cats with a section for each.
// Grief topic: warm, practical, no hard sell. PrintAWorld appears as one idea
// with its own section; its prices come from ./pet-figurines (petPricing), and
// it never claims real or flocked fur. No other company's products or prices
// are named.
const { petFigurines, petPricing } = require('./pet-figurines')

const [realistic] = petPricing.large

const groups = [
  {
    id: 'make-at-home',
    title: 'Keepsakes you can make at home',
    intro:
      'Most of these cost little or nothing, and several are easiest in the first days, while you still have their bed, blanket and brush.',
    ideas: [
      {
        title: 'A clay paw print',
        body: 'Press their paw into air-dry or oven-bake clay, or salt dough made from flour, salt and water. Press a straw through the top before it dries if you want to hang it. Many vet clinics will make one for you if you ask at the appointment.',
      },
      {
        title: 'An ink paw or nose print',
        body: 'A pet-safe ink pad and heavy paper give you a print you can frame, or send to a jeweler or tattoo artist later. Take several; the first one or two usually smudge.',
      },
      {
        title: 'A memory box or shadow box',
        body: 'Gather the collar and tag, a favorite toy, the paw print and a few photos in one box. A shadow box with a glass front lets you hang it on the wall instead of keeping it in a drawer.',
      },
      {
        title: 'A clipping of their fur',
        body: 'Trim a small lock from somewhere it won’t show, tie it with thread and keep it in a small envelope, a glass vial or a locket. Clip it before the last goodbye if you can; it is hard to ask for later.',
      },
      {
        title: 'A letter to them',
        body: 'Write down what you want to remember: how they greeted you, their habits, the funny and the ordinary days. Grief counselors often suggest it, and it becomes a record you will be glad to have.',
      },
      {
        title: 'A photo book of their life',
        body: 'Go through your phone from the first photo to the last and print a small book. Add dates and a line about each picture while you still remember the story behind it.',
      },
      {
        title: 'A pillow or small quilt from their blanket',
        body: 'Their blanket, or the old sweatshirt they always slept on, can be sewn into a cushion or a small quilt. A local seamstress or alterations shop can do it if you don’t sew.',
      },
      {
        title: 'A memory jar',
        body: 'Ask everyone who knew them to write a favorite memory on a slip of paper. Read them together, then keep the jar. Children often find this easier than talking.',
      },
    ],
  },
  {
    id: 'garden-memorials',
    title: 'Garden and outdoor memorial ideas',
    intro:
      'An outdoor memorial gives you somewhere to go. If you plan to bury your pet at home, check your city or county rules first; they vary.',
    ideas: [
      {
        title: 'Plant a tree or shrub',
        body: 'Choose something that flowers around the date you met them or the date they passed, so it marks the day every year. Ask a garden center what grows well where you live.',
      },
      {
        title: 'A pet memorial stone',
        body: 'An engraved stone with their name and dates sits in a garden bed, a planter or by the back door. Flat stones also work indoors, on a shelf or beside their photo.',
      },
      {
        title: 'A paw print stepping stone',
        body: 'Mix a bag of quick-setting concrete in a round mold, press in their paw print or a toy, and scratch in their name before it sets. If their paw is no longer an option, use the clay print as a stamp.',
      },
      {
        title: 'A flower bed in their favorite spot',
        body: 'Plant the corner where they liked to lie in the sun. Forget-me-nots, rosemary (for remembrance) or a flower in the color of their collar all make it theirs. Choose plants that are safe for any pets you still have.',
      },
      {
        title: 'A memorial bench or seat',
        body: 'A bench where you used to sit together, or a single chair in the garden, makes a quiet place to think about them. Some parks let you dedicate a bench; ask your local parks department.',
      },
      {
        title: 'A wind chime',
        body: 'A wind chime by a window or on the porch is a small, gentle reminder whenever the wind picks up. Some people hang their tag from it.',
      },
      {
        title: 'A bird feeder or birdbath',
        body: 'For a pet who loved watching the yard, a feeder or birdbath keeps the window busy and alive. Place it where you can see it from their old spot.',
      },
    ],
  },
  {
    id: 'photo-art',
    title: 'Photo and art memorial ideas',
    intro:
      'Good photos are the raw material for most of these, so back up every picture and video you have before anything else.',
    ideas: [
      {
        title: 'A painted or illustrated portrait',
        body: 'An artist can paint or draw your pet from photos, in a style that matches your home. Send your clearest photo of their face and one that shows their personality.',
      },
      {
        title: 'Frame one perfect photo',
        body: 'Pick the picture that is most them, print it large on good paper and frame it with their name and dates. It costs little and goes where you will see it every day.',
      },
      {
        title: 'A photo blanket or canvas',
        body: 'Print a favorite photo, or a collage, on a blanket or canvas. A blanket is something to hold on the hard evenings.',
      },
      {
        title: 'A memorial tattoo',
        body: 'Their paw print, nose print, name or portrait as a tattoo. Bring the ink print or a clear photo to the artist and ask to see their animal portrait work first.',
      },
      {
        title: 'A video of their life',
        body: 'String your short clips together with their favorite sounds: the bark at the door, the purr, the jingle of the collar. Free phone apps make this simple, and it is the closest thing to hearing them again.',
      },
      {
        title: 'A holiday ornament',
        body: 'An ornament with their photo, name or paw print goes on the tree every year. Many families add one for each pet they have loved.',
      },
    ],
  },
  {
    id: 'ashes-jewelry',
    title: 'Ashes keepsakes and memorial jewelry',
    intro:
      'If you choose cremation, you don’t have to decide what to do with the ashes right away. Many people keep them for months first.',
    ideas: [
      {
        title: 'An urn that fits your home',
        body: 'Urns come as boxes, vases, sculptures and wooden frames with a photo window. Pick something you’d be glad to see on a shelf, not tucked in a closet.',
      },
      {
        title: 'Cremation jewelry',
        body: 'A pendant, ring or bracelet with a tiny chamber holds a pinch of ashes or fur. Look for a screw that seals well if you’ll wear it every day.',
      },
      {
        title: 'A paw print engraved on jewelry',
        body: 'A jeweler can engrave their actual paw or nose print, from the ink print, onto a pendant, ring or bracelet.',
      },
      {
        title: 'Ashes set in glass or art',
        body: 'Glass artists can fold a small amount of ashes into a paperweight, an ornament or a bead. Ask how much they need; it is usually a spoonful, so the rest can stay in the urn.',
      },
      {
        title: 'Scatter them somewhere they loved',
        body: 'A beach, a hiking trail, the park where they ran. Rules for public land and private property vary, so ask permission where you need to, and keep a little back if family members may want some.',
      },
      {
        title: 'Plant them with a tree',
        body: 'Cremated ashes are very alkaline and salty, so mix them well with soil or compost rather than pouring them straight onto roots. A garden center can suggest how much to use for the space.',
      },
    ],
  },
  {
    id: 'giving-back',
    title: 'Donation and charity memorial ideas',
    intro:
      'Giving in their name turns some of the grief into help for another animal.',
    ideas: [
      {
        title: 'Donate to a shelter or rescue in their name',
        body: 'Most shelters and rescues accept memorial gifts and will send a card to someone you name. If your pet was adopted, their original rescue is a meaningful choice.',
      },
      {
        title: 'Give away their unused supplies',
        body: 'Unopened food, treats, beds, crates and gently used toys are often welcome at shelters. Call first to ask what they accept.',
      },
      {
        title: 'Foster or volunteer',
        body: 'When you’re ready, fostering or walking dogs and socializing cats at a shelter can help. It is also a gentle way to know when you’re ready for another pet, if you ever are.',
      },
      {
        title: 'Sponsor a shelter animal or kennel',
        body: 'Some shelters let you sponsor a kennel, a long-stay animal or an adoption fee, sometimes with their name on a plaque.',
      },
      {
        title: 'Support veterinary research',
        body: 'If your pet had cancer, kidney disease or another illness, a gift to a veterinary school or animal health research fund in their name can help the next pet with the same diagnosis.',
      },
    ],
  },
  {
    id: 'rituals',
    title: 'Rituals and simple ways to remember a pet',
    intro:
      'These cost nothing. They are small, repeatable ways of keeping them part of family life.',
    ideas: [
      {
        title: 'Hold a small goodbye',
        body: 'Gather the people who loved them, share stories, read the memory jar, or bury the ashes or plant the tree together. It helps children especially to have a moment that marks the loss.',
      },
      {
        title: 'Light a candle on the hard days',
        body: 'Their birthday, the day you brought them home, the anniversary of their passing. A candle by their photo is a small, private way to mark it.',
      },
      {
        title: 'Walk their route',
        body: 'Take the walk you always took together. It is painful at first, and many people say it later becomes the time they feel closest to their pet.',
      },
      {
        title: 'Write their story and share it',
        body: 'Post a short tribute with your favorite photos, or send it to the people who knew them. Friends will often reply with memories you’d forgotten.',
      },
      {
        title: 'Thank the people who cared for them',
        body: 'A note to your vet, groomer, walker or pet sitter. They grieve too, and they rarely hear how much they mattered.',
      },
      {
        title: 'Reach out for support',
        body: 'Pet loss is real grief. Your vet can point you to a pet loss support group or hotline, and many people find online pet loss communities helpful in the middle of the night.',
      },
    ],
  },
]

const figurineIdea = {
  id: 'figurine',
  title: 'A custom figurine or statue sculpted from their photos',
}

const dogIdeas = [
  {
    title: 'Hang their leash by the door',
    body: 'Their leash and collar on the hook where they always hung, or in a frame, keep the daily ritual visible. Some people add a small plaque with the name.',
  },
  {
    title: 'Keep a tennis ball or favorite toy in the shadow box',
    body: 'The chewed ball or the squeaky toy they carried everywhere says more about a dog than any photo.',
  },
  {
    title: 'A statue of them in their signature pose',
    body: 'The head tilt, the sit at the door, the way they slept with one paw over their nose. A figure of your dog in that pose, on a desk or in the garden, is a keepsake people hold onto for decades.',
  },
  {
    title: 'Run or walk a charity event for them',
    body: 'Many shelters hold dog walks and fun runs. Join in their name, or sponsor a friend who runs with their own dog.',
  },
  {
    title: 'A paw print on a garden stone at the gate',
    body: 'Put the stepping stone where they waited for you, by the gate or the back door.',
  },
]

const catIdeas = [
  {
    title: 'Keep their sunny spot',
    body: 'Leave a cushion, a small plant or their photo on the windowsill or chair they claimed. Cats make spots theirs, and leaving it as it was can be a comfort.',
  },
  {
    title: 'Plant catnip or cat grass',
    body: 'A pot of catnip or cat grass in their memory is a living reminder. If you have other cats, they will enjoy it too.',
  },
  {
    title: 'Make something from their collar bell',
    body: 'The little bell that announced them can go on a keyring, a wind chime or the tree at the holidays.',
  },
  {
    title: 'A sleeping cat figurine',
    body: 'Curled up, tail over the nose, the way they slept on your bed. A small figure of your cat in that pose sits on a nightstand or bookshelf.',
  },
  {
    title: 'Donate to a cat rescue or TNR program',
    body: 'Cat rescues and trap-neuter-return groups depend on donations, food and blankets. A gift in their name helps the cats still outside.',
  },
]

const giftIdeas = [
  {
    title: 'A handwritten card that uses the pet’s name',
    body: 'Say their name and share one memory, even a small one. It means more than any gift, and almost nobody does it.',
  },
  {
    title: 'A framed photo of their pet',
    body: 'Find a good photo from social media or ask a family member, and frame it. It is simple and almost always welcome.',
  },
  {
    title: 'A memorial stone or wind chime',
    body: 'Something for the garden or porch, engraved with the pet’s name. A good choice when you don’t know the person’s home well.',
  },
  {
    title: 'A plant or tree to plant',
    body: 'A potted plant, a flowering shrub or a tree sapling gives them something to tend. Check it is safe for any pets still in the house.',
  },
  {
    title: 'A donation in the pet’s name',
    body: 'A gift to their shelter or rescue, with a card telling them you did it.',
  },
  {
    title: 'A custom portrait or figurine',
    body: 'A painted portrait or a figurine sculpted from photos is a more personal memorial gift for someone who lost a dog or cat. Ask a family member for photos so it can be a surprise, and allow a few weeks.',
  },
  {
    title: 'Practical help',
    body: 'A meal, an offer to sit with them, or help picking up the ashes. In the first week, showing up matters most.',
  },
]

const ideaCount =
  groups.reduce((total, group) => total + group.ideas.length, 0) +
  1 +
  dogIdeas.length +
  catIdeas.length +
  giftIdeas.length

const petMemorialIdeas = {
  path: '/guides/pet-memorial-ideas/',
  title: `${ideaCount} Pet Memorial Ideas to Remember a Dog or Cat`,
  cardTitle: `${ideaCount} pet memorial ideas for dogs and cats`,
  publishedDate: '2026-11-02',
  reviewedDate: '2026-11-02',
  reviewedDateLabel: 'November 2, 2026',
}

const metaTitle = `${ideaCount} Pet Memorial Ideas for Dogs and Cats`
const description = `${ideaCount} pet memorial ideas for dogs and cats: paw prints and keepsakes to make at home, garden stones, ashes keepsakes, rituals and gifts for a grieving friend.`
const answer = `The best pet memorial ideas hold a specific memory: a clay paw print, a tree in their favorite spot, a shadow box with their collar, a donation to a shelter in their name, or a figurine sculpted from their photos. Here are ${ideaCount} ideas for dogs and cats, most free or inexpensive.`

const figurine = {
  smallSize: petPricing.smallSize,
  painted: `$${petPricing.smallPainted}`,
  singleColor: `$${petPricing.smallSingleColor}`,
  realisticSize: realistic.size,
  realisticPrice: realistic.price,
  modeling: `$${petPricing.largeModeling}`,
  weeks: petPricing.smallWeeks,
  path: petFigurines.path,
}

const faqs = [
  {
    question: 'What is a good way to memorialize a pet?',
    answer:
      'Choose something tied to a real memory of them: a paw print, their collar in a shadow box, a tree or flower bed in their favorite spot, a framed photo, or a donation to a shelter in their name. Many families do two things: one keepsake at home and one small ritual, like lighting a candle on their birthday.',
  },
  {
    question: 'What can I do with my pet’s ashes?',
    answer:
      'Keep them in an urn that fits your home, put a pinch in cremation jewelry, have a glass artist set some in a paperweight or ornament, scatter them where your pet loved to be (with permission where needed), or mix them with soil when you plant a tree. You don’t have to decide right away, and you can divide them between several of these.',
  },
  {
    question: 'What is the best memorial gift for someone who lost a dog?',
    answer:
      'A handwritten card that uses the dog’s name and shares a memory is the gift people remember most. To go with it, a framed photo, an engraved garden stone, a donation to a shelter in the dog’s name, or a custom portrait or figurine made from photos are all thoughtful choices.',
  },
  {
    question:
      'Can you make a statue from photos of a pet that has passed away?',
    answer: `Yes. A sculptor can work from the photos you already have. At PrintAWorld we model your pet in 3D from your photos, send the model for you to approve, then print it in resin and paint it by hand. If some angles are missing, we work from what you have and you tell us what to change before it is printed.`,
  },
  {
    question: 'How much does a custom pet memorial statue cost?',
    answer: `At PrintAWorld, a ${figurine.smallSize} hand-painted pet figurine starts at ${figurine.painted}, or ${figurine.singleColor} in a single color. Realistic sculptures start at ${figurine.realisticPrice} at ${figurine.realisticSize}, plus a one-time ${figurine.modeling} 3D model. Your written quote confirms the price before work starts.`,
  },
  {
    question: 'What should I write in a pet sympathy card?',
    answer:
      'Use the pet’s name, say you are sorry, and share one specific memory or something you noticed about them: “I’ll always remember Max meeting me at the door with a shoe.” Avoid “it was just a dog” or rushing to talk about a new pet.',
  },
  {
    question: 'How do I make a paw print keepsake at home?',
    answer:
      'Roll out air-dry clay or salt dough (two parts flour to one part salt, plus water) about half an inch thick, press the paw in firmly and evenly, then let clay air-dry for a day or two, or bake salt dough in a low oven for a few hours. For a flat print, use a pet-safe ink pad and heavy paper, and take a few tries.',
  },
  {
    question: 'Can I bury my pet in my backyard?',
    answer:
      'It depends on where you live. Some cities and counties allow it with conditions, such as depth and distance from water, and some don’t allow it at all, especially for renters. Check your local rules or ask your vet. Cremation with a garden memorial is an alternative that works anywhere.',
  },
]

module.exports = {
  petMemorialIdeas,
  metaTitle,
  description,
  answer,
  groups,
  figurineIdea,
  dogIdeas,
  catIdeas,
  giftIdeas,
  ideaCount,
  figurine,
  faqs,
}
